import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AccountingApi } from '_api';
import { FIELD_KEYS } from '_constants/forms';
import { MESSAGE_TYPES } from '_constants/messages';
import { handleError, handleResponse } from '_helpers/api';

import { selectParamsByType } from './selector';

export const markMessages = createAsyncThunk(
  'order/markMessages',
  async function ({ customerId, type, ids }, { dispatch, rejectWithValue }) {
    try {
      await AccountingApi.markMessages({ customerId, messages: ids });

      if (type === MESSAGE_TYPES.announcement) {
        dispatch(getAnnouncements({ customerId }));
      }
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const getAnnouncements = createAsyncThunk(
  'order/getAnnouncements',
  async function ({ customerId }, { getState, rejectWithValue }) {
    try {
      const params = selectParamsByType(MESSAGE_TYPES.announcement)(getState());
      const response = await AccountingApi.getMessages({ customerId, ...params });
      const data = handleResponse(response);

      const allResponse = await AccountingApi.getMessages({ customerId, ...params, page: 1, count: data.totalResults });
      const allData = handleResponse(allResponse);

      const notReadIds = allData?.elements?.reduce((ids, { id, wasRead }) => {
        if (!wasRead) {
          ids.push(id);
        }

        return ids;
      }, []);

      return { ...data, notReadIds };
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

const initialState = {
  [MESSAGE_TYPES.announcement]: {
    params: {
      [FIELD_KEYS.page]: 1,
      [FIELD_KEYS.count]: 5,
      [FIELD_KEYS.type]: MESSAGE_TYPES.announcement,
    },
    data: {},
    error: null,
    loading: false,
    fetchCounter: 0,
  },
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    refreshParams(state, action) {
      const { type, params } = action.payload || {};

      if (state[type]) {
        state[type].params = {
          ...state[type].params,
          ...params,
        };
      }
    },
    resetData(state, action) {
      if (state[action.payload]) {
        state[action.payload].data = {};
      } else {
        const keys = Object.keys(initialState);

        keys.forEach((key) => {
          state[key] = {
            ...state[key],
            data: {},
          };
        });
      }
    },
    resetError(state, action) {
      if (state[action.payload]) {
        state[action.payload].error = null;
      } else {
        const keys = Object.keys(initialState);

        keys.forEach((key) => {
          state[key] = {
            ...state[key],
            error: null,
          };
        });
      }
    },
    resetFetchCounter(state, action) {
      if (state[action.payload]) {
        state[action.payload].fetchCounter = 0;
      } else {
        const keys = Object.keys(initialState);

        keys.forEach((key) => {
          state[key] = {
            ...state[key],
            fetchCounter: 0,
          };
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAnnouncements.pending, (state) => {
        state[MESSAGE_TYPES.announcement].error = null;
        state[MESSAGE_TYPES.announcement].loading = true;
      })
      .addCase(getAnnouncements.fulfilled, (state, action) => {
        state[MESSAGE_TYPES.announcement].data = action.payload;
        state[MESSAGE_TYPES.announcement].loading = false;
        state[MESSAGE_TYPES.announcement].fetchCounter += 1;
      })
      .addCase(getAnnouncements.rejected, (state, action) => {
        state[MESSAGE_TYPES.announcement].error = action.payload;
        state[MESSAGE_TYPES.announcement].loading = false;
      });
  },
});

export const { refreshParams, resetData, resetError, resetFetchCounter } = messagesSlice.actions;

export default messagesSlice.reducer;
